import { Idiomorph } from 'idiomorph';

/**
 * Intercepts same-page navigations that only change the query string — link
 * clicks (e.g. pagination) and GET form submissions (e.g. filters) — fetches
 * the target page in the background and morphs the matching region's contents
 * in place instead of doing a full navigation. The region is matched across the
 * fetched document by `id`, so the element must have one.
 *
 * Morphing (rather than replacing innerHTML) keeps live DOM state that the
 * server doesn't own: the element the user is interacting with stays focused,
 * and open dropdowns stay open across a filter change.
 */
class AsyncRegionElement extends HTMLElement {
  connectedCallback() {
    if (!this.id) {
      console.warn('<chh-async-region> requires an id to match content across pages.');
      return;
    }

    this._pathname = window.location.pathname;

    this._controller = new AbortController();
    const { signal } = this._controller;

    this.addEventListener('click', this._onClick, { signal });
    this.addEventListener('submit', this._onSubmit, { signal });
    this.addEventListener('async-region:navigate', this._onNavigateEvent, { signal });
    window.addEventListener('popstate', this._onPopState, { signal });
  }

  disconnectedCallback() {
    this._controller?.abort();
    this._fetchController?.abort();
  }

  _onClick = (e) => {
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
      return;
    }

    const link = e.target.closest('a[href]');
    if (!link || !this.contains(link)) {
      return;
    }

    if (link.target && link.target !== '_self') {
      return;
    }

    const url = new URL(link.href, window.location.href);
    if (!this._isSamePageQueryChange(url)) {
      return;
    }

    e.preventDefault();
    this._navigate(url.href, { updateHistory: true, scroll: true });
  };

  _onSubmit = (e) => {
    const form = e.target.closest('form');
    if (!form || !this.contains(form)) {
      return;
    }

    if (form.method && form.method.toLowerCase() !== 'get') {
      return;
    }

    const action = new URL(form.getAttribute('action') || window.location.href, window.location.href);
    const params = new URLSearchParams(new FormData(form));
    action.search = params.toString();

    if (action.origin !== window.location.origin || action.pathname !== window.location.pathname) {
      return;
    }

    // Don't scroll on filter changes — it's jarring to jump while toggling options.
    e.preventDefault();
    this._navigate(action.href, { updateHistory: true, scroll: false });
  };

  // A descendant (e.g. a filter dropdown toggling an option) requests a same-page
  // navigation to an already-built URL, without scrolling.
  _onNavigateEvent = (e) => {
    const href = e.detail?.href;
    if (!href) {
      return;
    }

    this._navigate(new URL(href, window.location.href).href, { updateHistory: true, scroll: false });
  };

  _onPopState = () => {
    if (window.location.pathname !== this._pathname) {
      return;
    }

    this._navigate(window.location.href, { updateHistory: false, scroll: false });
  };

  // After a morph, an <img> whose src changed keeps painting the old image
  // until the new file downloads — leaving new text next to a stale image. Hide
  // any image that isn't done loading and reveal it once it is, so the stale
  // image disappears immediately instead of lingering.
  _hideLoadingImages() {
    for (const img of this.querySelectorAll('img')) {
      if (img.complete) {
        continue;
      }

      img.style.visibility = 'hidden';
      const reveal = () => {
        img.style.visibility = '';
      };
      img.addEventListener('load', reveal, { once: true });
      img.addEventListener('error', reveal, { once: true });
    }
  }

  _isSamePageQueryChange(url) {
    return (
      url.origin === window.location.origin &&
      url.pathname === window.location.pathname &&
      url.search !== window.location.search
    );
  }

  async _navigate(href, { updateHistory, scroll }) {
    this._fetchController?.abort();
    this._fetchController = new AbortController();

    this.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(href, {
        signal: this._fetchController.signal,
        headers: { 'X-Requested-With': 'fetch' },
      });
      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const incoming = doc.getElementById(this.id);
      if (!incoming) {
        throw new Error(`No matching #${this.id} in fetched document.`);
      }

      Idiomorph.morph(this, incoming.innerHTML, {
        morphStyle: 'innerHTML',
        callbacks: {
          // The open/closed state of a dropdown is owned by the client, not the
          // server, so never let a morph toggle it.
          beforeAttributeUpdated: (attributeName, node) => {
            if (attributeName === 'open' && node.tagName === 'DETAILS') {
              return false;
            }
          },
        },
      });

      this._hideLoadingImages();

      if (scroll) {
        this.scrollIntoView({ block: 'start', behavior: 'smooth' });
      }

      if (updateHistory) {
        window.history.pushState({}, '', href);
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      }
      // Fall back to a full navigation so the link still works.
      window.location.assign(href);
    } finally {
      this.removeAttribute('aria-busy');
    }
  }
}

customElements.define('chh-async-region', AsyncRegionElement);

export default AsyncRegionElement;
